import React from 'react';
import { Badge, Icon } from '@amzn/awsui-components-console';

export type StatusBadgeVariant = 'denied' | 'pending' | 'approved' | 'invalid' | 'not_onboarded' | 'role_no_access';

export interface StatusBadgeProps {
  variant: StatusBadgeVariant;
  label?: string;
}

/**
 * StatusBadge - A small inline indicator for access status, request status, or validation results
 * 
 * Variants:
 * - denied: Red background with X icon
 * - pending: Amber/grey background with clock icon
 * - approved: Green background with checkmark icon
 * - invalid: Red background with dot icon
 * - not_onboarded: Blue background with info icon (table not in Glue catalog)
 * - role_no_access: Grey background with warning icon (ADDAPT role lacks access)
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({ variant, label }) => {
  const getVariantConfig = () => {
    switch (variant) {
      case 'denied':
        return {
          color: 'red' as const,
          icon: 'status-negative' as const,
          defaultLabel: 'Denied'
        };
      case 'pending':
        return {
          color: 'grey' as const,
          icon: 'status-pending' as const,
          defaultLabel: 'Pending'
        };
      case 'approved':
        return {
          color: 'green' as const,
          icon: 'status-positive' as const,
          defaultLabel: 'Approved'
        };
      case 'invalid':
        return {
          color: 'red' as const,
          icon: 'status-warning' as const,
          defaultLabel: 'Invalid'
        };
      case 'not_onboarded':
        return {
          color: 'blue' as const,
          icon: 'status-info' as const,
          defaultLabel: 'Not Onboarded'
        };
      case 'role_no_access':
        return {
          color: 'grey' as const,
          icon: 'status-warning' as const,
          defaultLabel: 'Role No Access'
        };
      default:
        return {
          color: 'grey' as const,
          icon: 'status-info' as const,
          defaultLabel: 'Unknown'
        };
    }
  };

  const config = getVariantConfig();
  const displayLabel = label || config.defaultLabel;

  return (
    <Badge color={config.color}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <Icon name={config.icon} size="small" />
        {displayLabel}
      </span>
    </Badge>
  );
};

export default StatusBadge;
